import { PieChart, Pie, Sector, Cell, Tooltip } from "recharts";
import { Panel } from "../../../../../components/Panel";
import { MonthSelect } from "./MonthSelect";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { YearFilter } from "./YearFilter";
import { commaSeparatedNumber, getUser } from "../../../../../helpers/utils";
import { api } from "../../../../../helpers/api";
import useResponsive from "../../../../../helpers/useResponsive";

const getDates = (month?: string | number, year?: string | number) => {
  const currentMonth = month ? Number(month) - 1 : new Date().getMonth();
  const currentYear = year ? Number(year) : new Date().getFullYear();
  const startDate = moment()
    .set("year", currentYear)
    .set("month", currentMonth)
    .startOf("month")
    .format("YYYY-MM-DD");
  const endDate = moment()
    .set("year", currentYear)
    .set("month", currentMonth)
    .endOf("month")
    .format("YYYY-MM-DD");
  return {
    startDate,
    endDate,
    month: `${currentMonth + 1}`,
    year: `${currentYear}`,
  };
};

const COLORS = [
  "#000",
  "#655CFE",
  "#B6B6BD",
  "#E9E9EB",
  "rgba(5, 193, 104, 1)",
  "rgba(15, 190, 250, 1)",
  "#FF4FA6",
];

const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
    props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius - 2}
        outerRadius={outerRadius + 2}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  );
};

const CustomTooltip = ({ active, payload, label, data }: any) => {
  const summaryData = payload?.[0]?.payload;
  const totalBookings = data?.bookingsCountByChannel?.reduce(
    (acc, curr) => acc + Number(curr.count || 0),
    0
  );
  const percentage = ((summaryData?.count / totalBookings) * 100).toFixed(0);
  return (
    <div className="z-[9999] p-2.5 bg-surface-primary rounded-lg shadow border border-border-secondary justify-start items-center gap-1.5 inline-flex">
      <div
        className="w-2 h-2 rounded-lg"
        style={{
          backgroundColor: summaryData?.fill,
        }}
      />
      <div className="text-content-secondary text-xs font-medium font-['Inter']">
        {summaryData?.payload?.channel} ({percentage}%)
      </div>
    </div>
  );
};

export function SummaryChart({ user }) {
  const { is2XLDesktop } = useResponsive();

  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const [filter, setFilter] = useState<any>({ ...getDates() });
  const containerRef = useRef(null);
  const [data, setData] = useState<any>({});

  const fetchMetrics = async () => {
    try {
      const userId = user?.id;
      if (!userId) return;
      const filters = {
        type: "SourceSummary",
        startDate: filter.startDate,
        endDate: filter.endDate,
      };
      const metrics = await api.getMetrics(userId, filters);
      setData(metrics);
    } catch (error) {
      console.log(error);
    }
  };

  const handleMonthChange = (month: string) => {
    setFilter((prev) => ({ ...prev, ...getDates(month, prev.year) }));
  };

  const handleYearChange = (year: string) => {
    setFilter((prev) => ({ ...prev, ...getDates(prev.month, year) }));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchMetrics();
  }, [filter, user?.id]);

  useEffect(() => {
    // Implement outside click handler
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !(containerRef.current as HTMLElement).contains(event.target as Node)
      ) {
        setActiveIndex(undefined);
      }
    }

    // Bind the event listener
    document.addEventListener("mouseover", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mouseover", handleClickOutside);
    };
  }, []);

  return (
    <Panel
      label="Segmentation"
      actions={
        <div className="flex items-center gap-1.5">
          <MonthSelect
            disabled={false}
            value={filter.month}
            onChange={handleMonthChange}
          />
          <YearFilter
            disabled={false}
            value={filter.year}
            onChange={handleYearChange}
          />
        </div>
      }
      className="h-[310px] tall3:h-[360px] tall3XL:h-[410px] flex flex-col"
      contentClassName="h-full"
    >
      <div className="grow-[1] flex flex-col h-full" ref={containerRef}>
        <div className="text-left w-full px-3 !pl-4 pt-1 text-content-tertiary text-2xs font-medium">
          You saved{" "}
          <span className="text-success font-bold">
            {commaSeparatedNumber(data?.otaCommissionSaved || 0, false)}
          </span>{" "}
          in OTA comms. vs. last year
        </div>
        <div className="relative w-full px-5 pb-4 h-full items-center flex justify-center">
          <div className="pointer-events-none absolute top-0 left-0 right-0 bottom-0 flex-col justify-center items-center gap-[5px] flex">
            <div className="text-center text-content-primary text-xl font-extrabold leading-none">
              {commaSeparatedNumber(
                Number(data?.directBookingsPercentage || 0),
                true
              )}
            </div>
            <div className="text-content-tertiary text-xs font-medium">
              Direct Bookings
            </div>
            <div
              className={`px-[5px] pt-1 pb-[5px] text-right text-2xs leading-[11px] tracking-[-0.66px] rounded-[3px] shadow border justify-center items-center flex ${
                data?.directBookingsPercentageChange < 0
                  ? "text-info border-info-light bg-info-light"
                  : "text-success border-success-light bg-success-light"
              }`}
            >
              {(data?.otaBookingsComparePercentage || 0) < 0
                ? "-"
                : (data?.otaBookingsComparePercentage || 0) > 0
                ? "+"
                : ""}
              {data?.directBookingsPercentageChange || 0}% vs STLY
            </div>
          </div>
          <PieChart
            width={is2XLDesktop ? 275 : 200}
            height={is2XLDesktop ? 275 : 200}
          >
            <Pie
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              data={data?.bookingsCountByChannel || []}
              cx={is2XLDesktop ? 135 : 95}
              cy={is2XLDesktop ? 135 : 100}
              innerRadius={is2XLDesktop ? 90 : 60}
              outerRadius={is2XLDesktop ? 115 : 75}
              fill="#8884d8"
              paddingAngle={0}
              dataKey="count"
              //   startAngle={360}
              //   endAngle={0}
              direction="anticlockwise"
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(undefined)}
            >
              {data?.bookingsCountByChannel?.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  strokeWidth={0}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip data={data} />} />
          </PieChart>
        </div>
      </div>
    </Panel>
  );
}
